import { Box, Typography, useTheme } from '@mui/material';
import { Cast, Genres } from '@/logic/services/movies';

import CastNameWithImg from '../movie-details/casting-name-with-img';
import CastTitle from '../movie-details/casting-title-component';
import CastTitleWithText from '../movie-details/casting-title-with-text';
import { DateTime } from '../../utility/luxon';
import { FC } from 'react';
import GenreTitle from '../movie-details/genre-title';
import Icon24 from './icon-24';
import { movieDetailsResponse } from '@/logic/services';
import { pxToRem } from '@/theme/typography';
import useFilmFormatter from '@/hooks/use-film-formatter';

const timeIcon = '/icons/movie-details/time.svg';
const AvatarImg = '/icons/side-menu/avatar.svg';

interface movieInfoProps {
  data: movieDetailsResponse;
}

export const MovieInfo: FC<movieInfoProps> = movieInfoProps => {
  const theme = useTheme();

  const { formattedDuration, durationWithRateWithRtl } = useFilmFormatter(
    movieInfoProps.data?.film
  );

  return (
    <Box
      component='section'
      sx={{
        width: '100%',
        background: theme.palette.layout.bgSecondary,
        borderRadius: '12px',
        px: pxToRem(40),
        py: pxToRem(53),
        [theme.breakpoints.down('laptop')]: {
          pt: 0,
          mb: pxToRem(16),
        },
      }}
    >
      <Typography
        component='h2'
        sx={{
          fontSize: pxToRem(28),
          fontWeight: '700',
          mb: pxToRem(32),
        }}
      >
        {movieInfoProps?.data?.film?.title}
      </Typography>

      <Box
        component='div'
        sx={{ display: 'flex', mb: pxToRem(21), alignItems: 'center' }}
      >
        {formattedDuration && <Icon24 icon={timeIcon} title='images.title.show_time' />}

        <Typography
          component='p'
          sx={{ fontSize: pxToRem(22), color: theme.palette.grey[50], ml: pxToRem(5) }}
        >
          {durationWithRateWithRtl}
        </Typography>
      </Box>
      <Box component='div' sx={{ display: 'flex', flexWrap: 'wrap', mb: pxToRem(29) }}>
        {movieInfoProps?.data?.film?.genres?.slice(0, 2).map((genre: Genres) => (
          <GenreTitle
            key={genre.id}
            title={genre.name}
            color={genre.fontColor}
            bgColor={genre.backgroundColor}
          />
        ))}
      </Box>

      <CastTitleWithText
        text={movieInfoProps?.data?.film?.synopsis}
        title='movie_details.synopsis'
      />
      <CastTitleWithText
        text={DateTime.fromISO(movieInfoProps?.data?.film?.releaseDate).toFormat(
          'dd/MM/yyyy'
        )}
        title='movie_details.release_date'
      />

      <Box component='div' sx={{ display: 'flex', flexWrap: 'wrap' }}>
        <Box component='div' sx={{ mr: pxToRem(30) }}>
          <CastTitleWithText
            text={movieInfoProps?.data?.film?.movieLang}
            title='movie_details.movie_language'
          />
        </Box>
        <CastTitleWithText
          text={movieInfoProps?.data?.film?.subtitle}
          title='movie_details.subtitle'
        />
      </Box>

      <CastTitle title='movie_details.director' />
      <Box component='div' sx={{ display: 'flex', flexWrap: 'wrap' }}>
        {movieInfoProps?.data?.film?.directors?.map((director: Cast) => (
          <CastNameWithImg
            img={director.imageUrl || AvatarImg}
            key={director.id}
            name={director.firstName + ' ' + director.lastName}
          />
        ))}
      </Box>

      <CastTitle title='movie_details.cast' />
      <Box component='div' sx={{ display: 'flex', flexWrap: 'wrap' }}>
        {movieInfoProps?.data?.film?.casts?.map((cast: Cast) => (
          <CastNameWithImg
            img={cast.imageUrl || AvatarImg}
            key={cast.id}
            name={cast.firstName + ' ' + cast.lastName}
          />
        ))}
      </Box>
    </Box>
  );
};

export default MovieInfo;
